/**
 * PeliPOINTS data hooks — PEL-835.
 *
 * Wraps the `api.pelipoints.*` surface from `@pelipost/consumer-api-client`
 * with TanStack Query so the Hub and Redeem pages share a single cache.
 *
 * `useWalletBalance` already exists in `useWallet.ts`; there is a separate
 * `usePelipointsBalance` here because the shape is different (loyalty points,
 * not dollars) and the invalidation boundary is different too — redeeming
 * a reward must NOT invalidate the wallet key and vice versa.
 *
 * Cache keys:
 *   ['pelipoints']                       — base (invalidate-all)
 *   ['pelipoints', 'balance']            — balance + lifetime earned/redeemed
 *   ['pelipoints', 'history', page]      — paginated transaction history
 *   ['pelipoints', 'rewards']            — available rewards catalog
 *   ['pelipoints', 'redemptions']        — recent redemption history
 */

import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryOptions,
} from '@tanstack/react-query';
import type {
  PeliPointsBalance,
  PeliPointsHistory,
  RedeemRewardResponse,
  Redemption,
  Reward,
} from '@pelipost/consumer-api-client';

import { api } from '../lib/api/client';

export const pelipointsKeys = {
  all: ['pelipoints'] as const,
  balance: () => ['pelipoints', 'balance'] as const,
  history: (page: number) => ['pelipoints', 'history', page] as const,
  rewards: () => ['pelipoints', 'rewards'] as const,
  redemptions: () => ['pelipoints', 'redemptions'] as const,
};

/** Current point balance + lifetime earned/redeemed totals. */
export function usePelipointsBalance(
  options?: Omit<UseQueryOptions<PeliPointsBalance>, 'queryKey' | 'queryFn'>,
) {
  return useQuery<PeliPointsBalance>({
    queryKey: pelipointsKeys.balance(),
    queryFn: () => api.pelipoints.getBalance(),
    ...options,
  });
}

/**
 * Alias kept for backwards compatibility with PEL-823 (HomePage chip).
 * Prefer `usePelipointsBalance` going forward.
 */
export const usePeliPointsBalance = usePelipointsBalance;

/** Paginated earn/redeem transaction history. */
export function usePelipointsHistory(
  page: number = 1,
  options?: Omit<UseQueryOptions<PeliPointsHistory>, 'queryKey' | 'queryFn'>,
) {
  return useQuery<PeliPointsHistory>({
    queryKey: pelipointsKeys.history(page),
    queryFn: () => api.pelipoints.getHistory(page),
    ...options,
  });
}

/** Rewards catalog (gift cards, commissary credit, donations, merch). */
export function useRewards(
  options?: Omit<UseQueryOptions<Reward[]>, 'queryKey' | 'queryFn'>,
) {
  return useQuery<Reward[]>({
    queryKey: pelipointsKeys.rewards(),
    queryFn: () => api.pelipoints.getRewards(),
    ...options,
  });
}

/** Recent redemption history (completed, pending, failed). */
export function useRedemptions(
  options?: Omit<UseQueryOptions<Redemption[]>, 'queryKey' | 'queryFn'>,
) {
  return useQuery<Redemption[]>({
    queryKey: pelipointsKeys.redemptions(),
    queryFn: () =>
      api.pelipoints.getRedemptions().catch(() => [] as Redemption[]),
    ...options,
  });
}

/**
 * Redeem a reward. Invalidates balance, history, and redemptions so the hub
 * picks up the new remaining balance, the new debit transaction, and the new
 * redemption row in one refetch round-trip.
 */
export function useRedeemReward() {
  const qc = useQueryClient();
  return useMutation<RedeemRewardResponse, Error, string>({
    mutationFn: (rewardId) => api.pelipoints.redeemReward(rewardId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: pelipointsKeys.all });
    },
  });
}
